import React from 'react';
import PropTypes from 'prop-types';
import { p as props, d as defaults } from './componentEl-c8b73b10.js';
import { a as addClassNames, c as classNamePropTypes } from './classNames-cc2dcbf1.js';
import { a as addSpacingClasses, v as verticalMarginPropTypes, d as verticalPaddingPropTypes } from './spacing-7e57ffb0.js';
import _extends from '@babel/runtime/helpers/extends';
import { a as addBorderClasses, b as borderPropTypes } from './border-27b45cc2.js';

/*
shared utils for Grid component
*/

// possible breakpoints
const breakpoints = ['xs', 'sm', 'md', 'lg'];

// column widths
const cols = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

// custom prop for column numbers
function colnumProps(props, propName, componentName) {
  /* eslint-disable react/destructuring-assignment */
  if (props[propName] > 12) {
    /* eslint-disable prefer-template */
    return new Error('Invalid prop `' + propName + '` supplied to' + ' `' + componentName + '`. Must be equal to or less than 12.');
  }
  return null;
}

/*
column for grid
*/
function Col({
  componentEl: Component,
  className,
  children,
  xs,
  sm,
  md,
  lg,
  xsOffset,
  smOffset,
  mdOffset,
  lgOffset,
  last,
  first,
  border,
  // eslint-disable-next-line react/prop-types
  style
}) {
  let cssClasses = ['st_col'];
  const additionalAttrs = {
    style
  };
  cssClasses = addClassNames(className, cssClasses);
  if (xs && xs !== true) cssClasses.push(`st_col-xs-${xs}`);
  if (xs === true) cssClasses.push('st_col-xs');
  if (sm && sm !== true) cssClasses.push(`st_col-sm-${sm}`);
  if (sm === true) cssClasses.push('st_col-sm');
  if (md && md !== true) cssClasses.push(`st_col-md-${md}`);
  if (md === true) cssClasses.push('st_col-md');
  if (lg && lg !== true) cssClasses.push(`st_col-lg-${lg}`);
  if (lg === true) cssClasses.push('st_col-lg');
  if (xsOffset) cssClasses.push(`st_col-xs-offset-${xsOffset}`);
  if (smOffset) cssClasses.push(`st_col-sm-offset-${smOffset}`);
  if (mdOffset) cssClasses.push(`st_col-md-offset-${mdOffset}`);
  if (lgOffset) cssClasses.push(`st_col-lg-offset-${lgOffset}`);
  if (last) cssClasses.push(`st_last-${last}`);
  if (first) cssClasses.push(`st_first-${first}`);
  cssClasses = addBorderClasses(border, cssClasses);
  if (border) {
    if (!additionalAttrs.style) additionalAttrs.style = {};
    if (border.left) {
      additionalAttrs.style['--st-grid-border-l'] = 'var(--st-border-width)';
    }
    if (border.right) {
      additionalAttrs.style['--st-grid-border-r'] = 'var(--st-border-width)';
    }
  }
  return /*#__PURE__*/React.createElement(Component, _extends({
    className: cssClasses.join(' ')
  }, additionalAttrs), children);
}

/*
between
around
bottom
middle
top
center
start
end

xs sm md lg
*/

Col.propTypes = {
  /** dom element or component to use for outermost wrapper */
  componentEl: props,
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /* size at XS breakpoint */
  xs: PropTypes.oneOf([true, ...cols]),
  /* size at SM breakpoint */
  sm: PropTypes.oneOf([true, ...cols]),
  /* size at MD breakpoint */
  md: PropTypes.oneOf([true, ...cols]),
  /* size at LG breakpoint */
  lg: PropTypes.oneOf([true, ...cols]),
  /* offset at XS breakpoint */
  xsOffset: PropTypes.oneOf(cols),
  /* offset at SM breakpoint */
  smOffset: PropTypes.oneOf(cols),
  /* offset at MD breakpoint */
  mdOffset: PropTypes.oneOf(cols),
  /* offset at LG breakpoint */
  lgOffset: PropTypes.oneOf(cols),
  /* move item to be last */
  last: PropTypes.oneOf(breakpoints),
  /* move item to be first */
  first: PropTypes.oneOf(breakpoints),
  /** whether column should show a border to the left or right */
  border: borderPropTypes
};
Col.defaultProps = {
  componentEl: defaults,
  className: null,
  xs: null,
  sm: null,
  md: null,
  lg: null,
  xsOffset: null,
  smOffset: null,
  mdOffset: null,
  lgOffset: null,
  last: null,
  first: null,
  border: null
};

/*
outermost wrapper for grid
*/
function Row({
  componentEl: Component,
  className,
  children,
  start,
  center,
  end,
  top,
  middle,
  bottom,
  around,
  between,
  reverse,
  xsCols,
  smCols,
  mdCols,
  lgCols,
  border
}) {
  let cssClasses = ['st_row'];
  cssClasses = addClassNames(className, cssClasses);
  if (start) cssClasses.push(`st_start-${start}`);
  if (center) cssClasses.push(`st_center-${center}`);
  if (end) cssClasses.push(`st_end-${end}`);
  if (top) cssClasses.push(`st_top-${top}`);
  if (middle) cssClasses.push(`st_middle-${middle}`);
  if (bottom) cssClasses.push(`st_bottom-${bottom}`);
  if (around) cssClasses.push(`st_around-${around}`);
  if (between) cssClasses.push(`st_between-${between}`);
  if (reverse) cssClasses.push('st_reverse');
  const otherProps = {};
  if (xsCols && xsCols !== 12) {
    otherProps.style = otherProps.style || {};
    otherProps.style['--st-grid-component-row-cols-xs'] = xsCols;
  }
  if (smCols && smCols !== 12) {
    otherProps.style = otherProps.style || {};
    otherProps.style['--st-grid-component-row-cols-sm'] = smCols;
  }
  if (mdCols && mdCols !== 12) {
    otherProps.style = otherProps.style || {};
    otherProps.style['--st-grid-component-row-cols-md'] = mdCols;
  }
  if (lgCols && lgCols !== 12) {
    otherProps.style = otherProps.style || {};
    otherProps.style['--st-grid-component-row-cols-lg'] = lgCols;
  }
  cssClasses = addBorderClasses(border, cssClasses);
  return /*#__PURE__*/React.createElement(Component, _extends({
    className: cssClasses.join(' ')
  }, otherProps), children);
}
Row.propTypes = {
  /** dom element or component to use for outermost wrapper */
  componentEl: props,
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** align items to start of row */
  start: PropTypes.oneOf(breakpoints),
  /** align items to start of center */
  center: PropTypes.oneOf(breakpoints),
  /** align items to end of row */
  end: PropTypes.oneOf(breakpoints),
  /** align items to top of row */
  top: PropTypes.oneOf(breakpoints),
  /** align items to middle of row */
  middle: PropTypes.oneOf(breakpoints),
  /** align items to bottom of row */
  bottom: PropTypes.oneOf(breakpoints),
  /** distribute space around items */
  around: PropTypes.oneOf(breakpoints),
  /** distribute space between items */
  between: PropTypes.oneOf(breakpoints),
  /** reverse order of items */
  reverse: PropTypes.bool,
  /** number of columns for row in XS breakpoint */
  xsCols: colnumProps,
  /** number of columns for row in Sm breakpoint */
  smCols: colnumProps,
  /** number of columns for row in Md breakpoint */
  mdCols: colnumProps,
  /** number of columns for row in Lg breakpoint */
  lgCols: colnumProps,
  /** whether row should show a border on the top or bottom */
  border: borderPropTypes
};
Row.defaultProps = {
  componentEl: defaults,
  className: null,
  start: null,
  center: null,
  end: null,
  top: null,
  middle: null,
  bottom: null,
  around: null,
  between: null,
  reverse: false,
  xsCols: null,
  smCols: null,
  mdCols: null,
  lgCols: null,
  border: null
};

/*
outermost wrapper for grid
*/
function Grid({
  componentEl: Component,
  fluid,
  className,
  children,
  margin,
  padding
}) {
  let cssClasses = [];
  cssClasses = addClassNames(className, cssClasses);
  cssClasses = addSpacingClasses({
    padding,
    margin,
    cssClasses,
    denylistMargin: ['all', 'horizontal', 'left', 'right'],
    denylistPadding: ['all', 'horizontal', 'left', 'right']
  });
  if (fluid) cssClasses.push('st_container-fluid');
  if (!fluid) cssClasses.push('st_container');
  return /*#__PURE__*/React.createElement(Component, {
    className: cssClasses.join(' ')
  }, children);
}
Grid.propTypes = {
  /** dom element or component to use for outermost wrapper */
  componentEl: props,
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** whether Grid takes up all available horizontal space, or has fixed width at breakpoints */
  fluid: PropTypes.bool,
  /** optional margin, VERTICAL only */
  margin: verticalMarginPropTypes,
  /** optional padding, VERTICAL only */
  padding: verticalPaddingPropTypes
};
Grid.defaultProps = {
  componentEl: defaults,
  className: null,
  fluid: true,
  margin: null,
  padding: null
};
Grid.Row = Row;
Grid.Row.displayName = 'Grid.Row';
Grid.Col = Col;
Grid.Col.displayName = 'Grid.Col';

export { Grid as G };
